import React, { useState, useEffect } from "react";
import { Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import api from "../../../services/api";

export default function NovoAgendamentoMaster({
  idClienteMaster,
  funcionarios,
  onAgendamentoCriado,
}) {
  const [form, setForm] = useState({
    clienteId: null,
    funcionarioId: null,
    servicoId: null,
  });

  const cardStyle = {
    backgroundColor: '#3c4049',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '25px',
    width: '95%',
    maxWidth: '1200px',
    margin: '0 auto',
  };

  const [clienteQuery, setClienteQuery] = useState("");
  const [clienteSuggestions, setClienteSuggestions] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Buscar clientes (autocomplete)
  const fetchClientes = async (query) => {
    if (!query || query.length < 3) {
      setClienteSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(
        `/Clientes/search?idClienteMaster=${idClienteMaster}&query=${encodeURIComponent(
          query
        )}`
      );
      setClienteSuggestions(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClienteChange = (e) => {
    const q = e.target.value;
    setClienteQuery(q);
    setForm((p) => ({ ...p, clienteId: null }));
    fetchClientes(q);
  };

  const selectCliente = (c) => {
    setForm((p) => ({ ...p, clienteId: c.id }));
    setClienteQuery(c.nome);
    setClienteSuggestions([]);
  };

  // Carregar serviços conforme funcionário selecionado
  useEffect(() => {
    const loadServicos = async () => {
      if (!form.funcionarioId) return;
      setServicos([]);
      setLoading(true);
      try {
        const res = await api.get(`/Servicos/funcionario/${form.funcionarioId}`);
        setServicos(res.data || []);
      } catch (err) {
        console.error("Erro ao carregar serviços:", err);
      } finally {
        setLoading(false);
      }
    };
    loadServicos();
  }, [form.funcionarioId]);

  // Buscar horários
  useEffect(() => {
    const fetchHorarios = async () => {
      if (!form.funcionarioId || !form.servicoId || !selectedDate) return;
      setAvailableTimes([]);
      setLoading(true);
      try {
        const res = await api.get("/Horarios/disponiveis", {
          params: {
            idClienteMaster,
            idFuncionario: form.funcionarioId,
            idServico: form.servicoId,
            from: selectedDate,
            to: selectedDate,
          },
        });
        const times = (res.data || [])
          .map((x, i) => ({
            id: i,
            hora: (x.hora ?? x.Hora ?? x.HoraDisponivel ?? "").substring(0, 5),
          }))
          .filter((t) => t.hora);
        setAvailableTimes(times);
      } catch (err) {
        console.error("Erro ao buscar horários:", err);
        setError("Erro ao buscar horários disponíveis.");
      } finally {
        setLoading(false);
      }
    };
    fetchHorarios();
  }, [form.funcionarioId, form.servicoId, selectedDate]);

  // Criar agendamento
  const criarAgendamento = async () => {
    try {
      if (!form.clienteId) return window.globalAlert("Selecione um cliente.", "info");
      if (!form.funcionarioId) return window.globalAlert("Selecione um funcionário.", "info");
      if (!form.servicoId) return window.globalAlert("Selecione um serviço.", "info");
      if (!selectedDate) return window.globalAlert("Escolha uma data.", "info");
      if (!selectedTime) return window.globalAlert("Escolha um horário.", "info");

      const dataIso = `${selectedDate}T${selectedTime}:00`;
      await api.post("/Agendamentos", {
        ClienteMasterId: idClienteMaster,
        ClienteId: form.clienteId,
        ServicoId: form.servicoId,
        FuncionarioId: form.funcionarioId,
        Data: dataIso,
      });

      window.globalAlert("Agendamento criado com sucesso!", "success");
      onAgendamentoCriado?.();
    } catch (err) {
      console.error(err);
      window.globalAlert("Erro ao criar agendamento.", "error");
    }
  };

  return (
    <Card className="shadow-lg" style={cardStyle}>
      {error && <Alert variant="danger">{error}</Alert>}

      <Form>
        {/* Funcionário */}
        <Form.Group className="mb-3">
          <Form.Label>Funcionário</Form.Label>
          <Form.Select
            value={form.funcionarioId || ""}
            onChange={(e) =>
              setForm((p) => ({ ...p, funcionarioId: Number(e.target.value) }))
            }
          >
            <option value="">Selecione...</option>
            {funcionarios.map((f) => (
              <option key={f.id} value={f.id}>
                {f.nome}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Cliente */}
        <Form.Group className="mb-3" style={{ position: "relative" }}>
          <Form.Label>Cliente</Form.Label>
          <Form.Control
            type="text"
            value={clienteQuery}
            onChange={handleClienteChange}
            placeholder="Digite o nome do cliente..."
          />
          {clienteSuggestions.length > 0 && (
            <ul
              className="list-group position-absolute w-100"
              style={{ zIndex: 1000 }}
            >
              {clienteSuggestions.map((c) => (
                <li
                  key={c.id}
                  className="list-group-item list-group-item-action"
                  onClick={() => selectCliente(c)}
                  style={{ cursor: "pointer" }}
                >
                  {c.nome}
                </li>
              ))}
            </ul>
          )}
        </Form.Group>

        {/* Serviço */}
        <Form.Group className="mb-3">
          <Form.Label>Serviço</Form.Label>
          <Form.Select
            value={form.servicoId || ""}
            onChange={(e) =>
              setForm((p) => ({ ...p, servicoId: Number(e.target.value) }))
            }
            disabled={!form.funcionarioId}
          >
            <option value="">Selecione...</option>
            {servicos.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome} {s.duracaoMinutos ? `(${s.duracaoMinutos} min)` : ""}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Data */}
        <Form.Group className="mb-3">
          <Form.Label>Data</Form.Label>
          <Form.Control
            type="date"
                value={selectedDate || ""}
                onChange={(e) => setSelectedDate(e.target.value)}
                onFocus={(e) =>
                  e.target.showPicker && e.target.showPicker()
                }

          />
        </Form.Group>

        {/* Horários */}
        <Form.Group className="mb-3">
          <Form.Label>Horários Disponíveis</Form.Label>
          {loading ? (
            <Spinner />
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {availableTimes.map((t) => (
                <Button
                  key={t.id}
                  size="sm"
                  variant={selectedTime === t.hora ? "success" : "outline-dark"}
                  onClick={() => setSelectedTime(t.hora)}
                >
                  {t.hora}
                </Button>
              ))}
            </div>
          )}
        </Form.Group>

        <div className="d-flex gap-2">
          <Button variant="primary" onClick={criarAgendamento}>
            Criar
          </Button>
          <Button variant="secondary" onClick={onAgendamentoCriado}>
            Cancelar
          </Button>
        </div>
      </Form>
    </Card>
  );
}
